/**
 * Customer generated-reports archive (usage / diagnosis PDFs and CSVs).
 */
import { useEffect, useState } from "react";
import adminStyles from "../ui/AdminStyles";
import { ThWithTip } from "../components/FieldTip";
import { authedGet } from "../api/customerAuthedFetch";

const TIPS = {
  file: "Report filename as stored in your organization’s report archive.",
  format: "File type of the generated report (PDF or CSV).",
  generated: "When this report file was last written to the archive.",
  size: "Size of the report file.",
  actions:
    "View opens the file in a new tab when possible. Download saves a copy. Links expire after a short time; click again if needed.",
};

function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "—";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CustomerReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    document.title = "Reports";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authedGet("/customer/reports")
      .then((data) => {
        if (!cancelled) setReports(data.items || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load reports");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function openReport(key, mode) {
    setBusyKey(key);
    try {
      const data = await authedGet(
        `/customer/reports/download?key=${encodeURIComponent(key)}`
      );
      const url = mode === "view" ? data.view_url : data.download_url;
      if (!url) throw new Error("No URL returned");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert(e.message || "Failed to open report");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div style={{ padding: 16, maxWidth: 960 }}>
      <h2 style={{ marginBottom: 8 }}>Reports</h2>
      <p style={{ marginTop: 0, fontSize: 13, color: "#6b7280" }}>
        Previously generated usage and diagnosis reports. Schedules are managed by
        AIxMed.
      </p>

      {loading && <p>Loading reports…</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {!loading && !error && reports.length === 0 && (
        <p style={{ color: "#6b7280" }}>No generated reports yet.</p>
      )}
      {!loading && reports.length > 0 && (
        <table style={adminStyles.table}>
          <thead>
            <tr>
              <ThWithTip tip={TIPS.file}>File</ThWithTip>
              <ThWithTip tip={TIPS.format}>Format</ThWithTip>
              <ThWithTip tip={TIPS.generated}>Generated</ThWithTip>
              <ThWithTip tip={TIPS.size}>Size</ThWithTip>
              <ThWithTip tip={TIPS.actions}>Actions</ThWithTip>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.key}>
                <td style={adminStyles.td}>{r.filename}</td>
                <td style={adminStyles.td}>{(r.format || "").toUpperCase()}</td>
                <td style={adminStyles.td}>
                  {r.last_modified
                    ? new Date(r.last_modified).toLocaleString()
                    : "—"}
                </td>
                <td style={adminStyles.td}>{formatBytes(r.size)}</td>
                <td style={adminStyles.td}>
                  <button
                    type="button"
                    disabled={busyKey === r.key}
                    onClick={() => openReport(r.key, "view")}
                    style={{ marginRight: 8 }}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    disabled={busyKey === r.key}
                    onClick={() => openReport(r.key, "download")}
                  >
                    Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
